import {
  AbstractInstructorNumbers,
  AbstractProgram,
  AbstractSession,
  AbstractUser,
  isSessionBookable,
  sortSessionsByStartDate,
  StripeProduct,
} from "@kvix/shared";
import { RichText, Text } from "@kvix/ui";
import { Box, Grid, Link, useMediaQuery, useTheme } from "@material-ui/core";
import React, { useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import queryString from "query-string";
import { KvixUserContext } from "../../../contexts/user";
import { useFetchInstructorNumbers } from "../../../hooks/http";
import { InstructorCard } from "../../partials/Instructor/InstructorCard";
import { ReplayList } from "../../partials/ReplayList";
import { SessionBooker } from "../../partials/SessionBooker";
import { ProgramFacebookGroup } from "./FacebookGroup";
import { PurchaseFlowContext } from "../../../contexts/PurchaseFlow";
import { hasRequiredProduct } from "src/contexts/user";
import Button from "@material-ui/core/Button";
import ReactMarkdown from "react-markdown";
import Typography from "@material-ui/core/Typography";
import { Flow, RegisterStep } from "../../PurchaseFlow";
import { CheckoutOptions, PaymentContext } from "../../pages/Payment/context";

interface Props {
  view: string;
  program: AbstractProgram;
  replays: AbstractSession[];
  classes: Record<"details" | "video" | "instructorCard", string>;
}

interface LinkReplacement {
  displayText: string;
  url: string;
}

const unregisteredTextRegex = /Unregistered:\{\{(.+?)\}\}\{\{(.+?)\}\}/;
const registeredTextRegex = /Registered:\{\{(.+?)\}\}\{\{(.+?)\}\}/;
const payingTextRegex = /Paying:\{\{(.+?)\}\}(?:\{\{(.+?)\}\})?/;

////Unregistered:{{Registrera dig på Kvix!}}{{/signup}} Registered:{{Köp Order66 nu!}}{{PRODUCT_ID}} Paying:{{Superspecialpris only for you}}{{PRODUCT_ID}} 
const replaceTextForUserType = (description: string, kvixUser: AbstractUser | null) => {
  let linkToInsert = "";

  if (!kvixUser) {
    const unregisteredMatch = description.match(unregisteredTextRegex);
    if (unregisteredMatch) {
      linkToInsert = `[${unregisteredMatch[1]}](${unregisteredMatch[2]})`;
      description = description.replace(unregisteredTextRegex, `${linkToInsert}`)
        .replace(registeredTextRegex, '')
        .replace(payingTextRegex, '');
    }
  } else {
    if (hasRequiredProduct(kvixUser, StripeProduct.LIVE) && !hasRequiredProduct(kvixUser, StripeProduct.HABIT66) ) {
      const payingMatch = description.match(payingTextRegex);
      if (payingMatch) {
        linkToInsert = `[${payingMatch[1]}](${payingMatch[2] || ''})`;
        description = description.replace(payingTextRegex, `${linkToInsert}`)
          .replace(registeredTextRegex, '')
          .replace(unregisteredTextRegex, '');
      }
    } else if (!hasRequiredProduct(kvixUser, StripeProduct.LIVE) && !hasRequiredProduct(kvixUser, StripeProduct.HABIT66)) {
      const registeredMatch = description.match(registeredTextRegex);
      if (registeredMatch) {
        linkToInsert = `[${registeredMatch[1]}](${registeredMatch[2]})`;
        description = description.replace(registeredTextRegex, `${linkToInsert}`)
          .replace(payingTextRegex, '')
          .replace(unregisteredTextRegex, '');
      }
    } else {
      description = description
        .replace(registeredTextRegex, '')
        .replace(payingTextRegex, '')
        .replace(unregisteredTextRegex, '');
    }
  }

  return {
    description: description,
    buttonText: linkToInsert ? linkToInsert.match(/\[(.+?)\]/)?.[1] : '',
    buttonUrl: linkToInsert ? linkToInsert.match(/\((.+?)\)/)?.[1] : '',
  };
};

export const About: React.FC<Props> = (props) => {
  const { t, ready } = useTranslation("program");
  const userContext = useContext(KvixUserContext);
  const { actions } = useContext(PurchaseFlowContext);
  const paymentContext = useContext(PaymentContext);
  const instructorNumbers: AbstractInstructorNumbers = useFetchInstructorNumbers(+props.program.instructor.id);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const searchParams = queryString.parse(window.location.search);

  const { description } = replaceTextForUserType(props.program.description, userContext.user);

  const handleCheckout = async (checkoutOptions: CheckoutOptions) => {
    await paymentContext.actions.checkout(checkoutOptions);
  };

  useEffect(() => {
    if (props.program.requiredProduct === StripeProduct.HABIT66 && searchParams.productSignup) {
      
      const newUserPrice = props.program.description.match(registeredTextRegex)[2];
  
      (async() => {
        await handleCheckout({
          cancel_url: `${window.location.origin}${window.location.pathname}`,
          referrer: `${window.location.origin}${window.location.pathname}?productSignupSuccess=true`,
          priceId: newUserPrice,
        });
      })();
    }
  }, [window.location.search]);

  const LinkRenderer = ({ href, children }) => {
    const handleClick = async(e: React.SyntheticEvent) => {
      e.preventDefault();

      if (!userContext.user) {
        actions.prompt(
          <Flow>
            { props.program.requiredProduct === StripeProduct.HABIT66 ?
              <RegisterStep tabState="register" productSignup={true} hidePrice />
              :
              <RegisterStep tabState="register" />
            }
          </Flow>
        );
        return;
      }

      await handleCheckout({
        cancel_url: window.location.href,
        referrer: window.location.href,
        priceId: href,
      });
    };

    if (href.includes('price_') || href.includes('signup')) {
      return (
        <Button
          color="primary"
          onClick={handleClick}
          href={href}
          style={{color: theme.palette.primary.main}}
        >
          {children}
        </Button>
      );
    } else {
      return (
        <Link
          href={href}
          target="_blank"
          rel="noopener"
        >{children}</Link>
      )
    }
  };

  const sessions = useMemo(() => {
    return props.program.sessions
      .filter(isSessionBookable)
      .sort(sortSessionsByStartDate);
  }, [props.program.sessions]);

  if (!ready) {
    return null;
  }

  return (
    <Grid item xs={12}>
      <Grid container spacing={6} justify="space-between">
        <Grid item style={{ flexGrow: 1, flexBasis: 400 }}>
          <Grid container direction="column" spacing={8}>
            <Grid item>
              <Text variant="h4" gutterBottom>
                {t("aboutClass")}
              </Text>
              <Typography component="div">
              { props.program.description.includes('price') ?
                <ReactMarkdown renderers={{
                  link: LinkRenderer,
                }}>{description}
                </ReactMarkdown> : 
                <RichText text={props.program.description} noPadding />
              }
              </Typography>
              
            </Grid>

            {props.program?.goodToKnow?.length > 0 && (
              <Grid item>
                <Text variant="h4" gutterBottom>
                  {t("goodToKnow")}
                </Text>

                <Text component="ul">
                  {props.program.goodToKnow.map((goodToKnow, i) => {
                    return <li key={i}>{goodToKnow}</li>;
                  })}
                </Text>
              </Grid>
            )}

            {props.view === "program" ? (
              <Grid item>
                <div id="sessionBooker" />
                <SessionBooker
                  program={props.program}
                  sessions={sessions}
                  onSubmit={(sessions) =>
                    userContext.actions.bookSessions(sessions)
                  }
                  onCancel={(sessions) =>
                    userContext.actions.cancelSessions(sessions)
                  }
                  requiredProduct={props.program.requiredProduct}
                />
              </Grid>
            ) : null}

            {props.program.facebookGroupId && (
              <Grid item>
                <ProgramFacebookGroup
                  groupId={props.program.facebookGroupId}
                  isMobile={isMobile}
                />
              </Grid>
            )}

            {props.replays.length > 0 && (
              <Grid item style={{ width: "100%" }}>
                <div id="latestSession" />

                <Text variant="h4" gutterBottom>
                  {t("latestClasses.title")}
                </Text>

                <Text>{t("latestClasses.description")}</Text>

                <ReplayList
                  replays={props.replays.map((replay) => {
                    return { ...replay, program: props.program };
                  })}
                  initialReplays={4}
                  programs={[props.program]}
                />
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid item className={props.classes.instructorCard}>
          <Box position="sticky" top={100}>
            <InstructorCard
              instructor={props.program.instructor}
              instructorNumbers={instructorNumbers}
            />
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
};
